import React, { Component, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { startVersionPolling, isNewVersionAvailable } from './lib/version-check';
import './index.css';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Intercept navigation: if a new version was deployed, do a full page
// load instead of a client-side transition. The user seamlessly gets
// the new version without seeing a banner or prompt.
//
// onBeforeNavigate fires before the router starts loading the new route.
// Setting window.location.href aborts all in-flight JS (including the
// client-side nav), so the hard reload always wins. The chunk error
// boundary acts as a safety net for any edge case.
router.subscribe('onBeforeNavigate', ({ toLocation }) => {
  if (isNewVersionAvailable() && toLocation) {
    window.location.href = toLocation.href;
  }
});

/**
 * Catches chunk load failures after a deploy (old hashed chunks are gone)
 * and reloads the page once to fetch the new version.
 */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(error: Error) {
    const msg = error.message;
    if (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Unable to preload CSS')
    ) {
      return { hasError: true };
    }
    throw error;
  }

  componentDidCatch() {
    const reloaded = sessionStorage.getItem('chunk-reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

// Clear the reload guard on successful load
sessionStorage.removeItem('chunk-reload');

// Start polling for version changes
startVersionPolling();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ChunkErrorBoundary>
      <RouterProvider router={router} />
    </ChunkErrorBoundary>
  </React.StrictMode>
);
